export interface IncomeFormFirmEtcKey {
    key: string;
    title: string;
    type: 'amount' | 'text';
    order: number;
    types?: string[];
}

/** API type codes from income-head/firm-etc */
export const FIRM_ETC_TYPE_KEY = 'IS_FE_ETC_TYPE';

export const FIRM_ETC_TYPE_FIRM = '8';
export const FIRM_ETC_TYPE_AOP = '9';
export const FIRM_ETC_TYPE_FOREIGN = '10';
export const FIRM_ETC_TYPE_SPOUSE = '11';

export const FIRM_AOP_TYPES = [FIRM_ETC_TYPE_FIRM, FIRM_ETC_TYPE_AOP];

export const FIRM_ETC_TYPE_LABELS: Record<string, string> = {
    [FIRM_ETC_TYPE_FIRM]: 'Firm',
    [FIRM_ETC_TYPE_AOP]: 'AoP',
    [FIRM_ETC_TYPE_FOREIGN]: 'Foreign Income',
    [FIRM_ETC_TYPE_SPOUSE]: 'Spouse/Minor Income',
};

/**
 * Text / identity fields shown above the Particulars table.
 * Amount fields are filtered into the 2-column table by type.
 */
export const INCOME_FORM_FIRM_ETC_KEYS: IncomeFormFirmEtcKey[] = [
    { key: FIRM_ETC_TYPE_KEY, title: 'Income Type', type: 'text', order: 1 },

    // Firm / AoP / Foreign share the same name key with different labels
    { key: 'IS_FE_NAME', title: 'Name of the Firm', type: 'text', order: 2, types: [FIRM_ETC_TYPE_FIRM] },
    { key: 'IS_FE_NAME', title: 'Name of the AoP', type: 'text', order: 2, types: [FIRM_ETC_TYPE_AOP] },
    { key: 'IS_FE_NAME', title: 'Name of the Employer/Company', type: 'text', order: 2, types: [FIRM_ETC_TYPE_FOREIGN] },

    { key: 'IS_FE_TIN_OF_FIRM_AOP', title: 'TIN of the Firm', type: 'text', order: 3, types: [FIRM_ETC_TYPE_FIRM] },
    { key: 'IS_FE_TIN_OF_FIRM_AOP', title: 'TIN of the AoP', type: 'text', order: 3, types: [FIRM_ETC_TYPE_AOP] },

    { key: 'IS_FE_CHK_TAX_PAID_TOTAL_INCM_BY_FIRM_AOP', title: 'Tax Paid on Total Income by the Firm', type: 'text', order: 4, types: [FIRM_ETC_TYPE_FIRM] },
    { key: 'IS_FE_CHK_TAX_PAID_TOTAL_INCM_BY_FIRM_AOP', title: 'Tax Paid on Total Income by the AoP', type: 'text', order: 4, types: [FIRM_ETC_TYPE_AOP] },

    { key: 'IS_FE_TAXPAYER_SHARE_OF_INCM', title: "Taxpayer's Share of Income (%)", type: 'text', order: 5, types: FIRM_AOP_TYPES },

    { key: 'IS_FE_SOURCE_OF_INCM', title: 'Source of Income', type: 'text', order: 1, types: [FIRM_ETC_TYPE_FOREIGN] },
    { key: 'IS_FE_COUNTRY_OF_ORIGIN', title: 'Country of Origin', type: 'text', order: 3, types: [FIRM_ETC_TYPE_FOREIGN] },
    { key: 'IS_FE_CHK_TAX_PAID_TOTAL_INCM_BY_FIRM_AOP', title: 'Tax Paid in abroad?', type: 'text', order: 4, types: [FIRM_ETC_TYPE_FOREIGN] },
    { key: 'IS_FE_REF_NO', title: 'Reference No.', type: 'text', order: 5, types: [FIRM_ETC_TYPE_FOREIGN] },
    { key: 'IS_FE_REF_DATE', title: 'Reference Date', type: 'text', order: 6, types: [FIRM_ETC_TYPE_FOREIGN] },

    { key: 'IS_FE_NAME_OF_SPOUSE', title: 'Name of the Spouse/Child', type: 'text', order: 8, types: [FIRM_ETC_TYPE_SPOUSE] },
    { key: 'IS_FE_DOB_OF_SPOUSE', title: 'Date of Birth', type: 'text', order: 9, types: [FIRM_ETC_TYPE_SPOUSE] },
    { key: 'IS_FE_RELATION_OF_SPOUSE', title: 'Relationship', type: 'text', order: 10, types: [FIRM_ETC_TYPE_SPOUSE] },
    { key: 'IS_FE_SRC_INCOME_OF_SPOUSE', title: 'Source of Income', type: 'text', order: 11, types: [FIRM_ETC_TYPE_SPOUSE] },

    // Amounts — Firm
    { key: 'IS_FE_TOTAL_INCM', title: 'Total Income of the Firm', type: 'amount', order: 20, types: [FIRM_ETC_TYPE_FIRM] },
    { key: 'IS_FE_IFEMP_REM_RECEIVED_TAXPAYER', title: 'Salary/Remuneration Received by the Taxpayer', type: 'amount', order: 21, types: [FIRM_ETC_TYPE_FIRM] },
    { key: 'IS_FE_IFEMP_REM_RECEIVED_OTHER', title: 'Salary/Remuneration Received by Other Partners', type: 'amount', order: 22, types: [FIRM_ETC_TYPE_FIRM] },
    { key: 'IS_FE_SHARE_OF_INCM', title: 'Share of Income', type: 'amount', order: 23, types: [FIRM_ETC_TYPE_FIRM] },
    { key: 'IS_FE_SHARE_OF_INCM_NT', title: 'Share of Firm Income (Not Taxed)', type: 'amount', order: 24, types: [FIRM_ETC_TYPE_FIRM] },
    { key: 'IS_FE_SHARE_OF_INCM_T', title: 'Share of Firm Income (Taxed)', type: 'amount', order: 25, types: [FIRM_ETC_TYPE_FIRM] },

    // Amounts — AoP
    { key: 'IS_FE_TOTAL_INCM', title: 'Total Income of the AoP', type: 'amount', order: 20, types: [FIRM_ETC_TYPE_AOP] },
    { key: 'IS_FE_IFEMP_REM_RECEIVED_TAXPAYER', title: 'Salary/Remuneration Received by the Taxpayer', type: 'amount', order: 21, types: [FIRM_ETC_TYPE_AOP] },
    { key: 'IS_FE_IFEMP_REM_RECEIVED_OTHER', title: 'Salary/Remuneration Received by Other Members', type: 'amount', order: 22, types: [FIRM_ETC_TYPE_AOP] },
    { key: 'IS_FE_SHARE_OF_INCM', title: 'Share of Income', type: 'amount', order: 23, types: [FIRM_ETC_TYPE_AOP] },
    { key: 'IS_FE_SHARE_OF_INCM_NT', title: 'Share of AoP Income (Not Taxed)', type: 'amount', order: 24, types: [FIRM_ETC_TYPE_AOP] },
    { key: 'IS_FE_SHARE_OF_INCM_T', title: 'Share of AoP Income (Taxed)', type: 'amount', order: 25, types: [FIRM_ETC_TYPE_AOP] },

    // Amounts — Foreign Income
    { key: 'IS_FE_TOTAL_INCM', title: 'Gross Foreign Income', type: 'amount', order: 20, types: [FIRM_ETC_TYPE_FOREIGN] },
    { key: 'IS_FE_AMT_FOREIGN_TAX_PAID', title: 'Amount of Foreign Tax Paid', type: 'amount', order: 21, types: [FIRM_ETC_TYPE_FOREIGN] },
    { key: 'IS_FE_FOREIGN_INCM', title: 'Net Foreign Income', type: 'amount', order: 22, types: [FIRM_ETC_TYPE_FOREIGN] },

    // Amounts — Spouse/Minor
    { key: 'IS_FE_NET_INCM', title: 'Net Income of Spouse/Minor Children (Not Assessed Separately)', type: 'amount', order: 20, types: [FIRM_ETC_TYPE_SPOUSE] },
];
